import { getParentDepartment } from '@moonpig/web-core-utils'
import { BRAND_LABEL } from '@moonpig/web-core-brand/config'
import { ViewItemEvent, ViewItemListEvent } from '@moonpig/web-core-analytics'
import { getCurrencyCode } from '@moonpig/web-shared-utils'
import { AddonsFieldsFragment } from '../queries/fragments/__generated__/addonsFragment'
import { isSponsoredCheck } from './utils'

export type VariantInfo = {
  addons?: AddonsFieldsFragment[] | null
  price: {
    currencyCode: string
  }
}

type Product = {
  id: string
  category: string
  title: string
  price: number
  index: number
  categoryDepartment?: string
  inStock: boolean
  variantTitle?: string
  isSponsored?: boolean
  internallyPromoted?: boolean
  dynamicImages?: {
    intentKey: string
    large: {
      webp: { url: string; title: string }[]
    }
    medium: {
      webp: { url: string; title: string }[]
    }
  } | null
}

type ProductViewItemEventProps = {
  product: Product
  variants: VariantInfo[]
  pageLocation: string
}

type ProductItemAddonsProps = {
  products: AddonsFieldsFragment[]
  pageLocation: string
}

export const createViewItemsAddonsGAEvent = ({
  products,
  pageLocation,
}: ProductItemAddonsProps): ViewItemListEvent => {
  const value = Number(
    products.reduce((total, product) => total + product.price.centAmount, 0) /
      100,
  )
  const listName = pageLocation.trim()
  return {
    event: 'view_item_list',
    ecommerce: {
      currency: getCurrencyCode(
        products[0].price.currencyCode.toLocaleUpperCase(),
      ),
      value,
      items: products.map((product, index) => ({
        index: index + 1,
        item_brand: BRAND_LABEL.toLowerCase(),
        item_category: 'add-ons',
        item_category2: undefined,
        item_category3: product.inStock
          ? /* istanbul ignore next */ 'in-stock'
          : /* istanbul ignore next */ 'out-of-stock',
        item_category4: undefined,
        item_category5: undefined,
        item_id: product.sku.toLowerCase(),
        item_list_name: listName.toLowerCase(),
        item_name: product.title.toLowerCase(),
        item_variant: undefined,
        price: Number(product.price.centAmount / 100),
        quantity: 1,
      })),
    },

    event_data: {
      category: 'product action',
      action: 'view item list',
      label: `${listName} | make it complete | carousel`.toLowerCase(),
      value: Math.round(value),
    },
  }
}

export const createViewItemGAEvent = ({
  product,
  variants,
  pageLocation,
}: {
  product: Product
  variants: VariantInfo[]
  pageLocation: string
}): ViewItemEvent => {
  const value = Number(product.price / 100)
  const listName = pageLocation.trim()

  return {
    event: 'view_item',
    ecommerce: {
      currency: getCurrencyCode(
        variants[0].price.currencyCode.toLocaleUpperCase(),
      ),
      value,
      items: [
        {
          index: 1,
          item_brand: BRAND_LABEL.toLowerCase(),
          item_category: product.category.toLowerCase(),
          item_category2: product.categoryDepartment
            ? /* istanbul ignore next */ getParentDepartment(
                product.categoryDepartment,
              ).toLowerCase()
            : /* istanbul ignore next */ undefined,
          item_category3: product.inStock
            ? /* istanbul ignore next */ 'in-stock'
            : /* istanbul ignore next */ 'out-of-stock',
          item_category4: undefined,
          item_category5: undefined,
          item_id: product.id.toLowerCase(),
          item_list_id: undefined,
          item_list_name: 'product details',
          item_name: product.title.toLowerCase(),
          item_variant: product?.variantTitle?.toLowerCase() || undefined,
          price: Number(product.price / 100),
          quantity: 1,
          is_sponsored: isSponsoredCheck(
            product.isSponsored,
            product.internallyPromoted,
          ),
        },
      ],
    },

    event_data: {
      category: 'product action',
      action: 'view item',
      label: `${listName} | ${product.id.toLowerCase()}`,
      value: Math.round(value),
    },
  }
}

export const createViewItemsWithAddonsGAEvent = ({
  product,
  variants,
  pageLocation,
}: ProductViewItemEventProps) => {
  return createViewItemGAEvent({
    product,
    variants,
    pageLocation,
  })
}
